import type { IAccount } from "@/modules/account/common/types";
import { useGetCardTransferTemplateQuery } from "@/modules/template/services";
import { cardTransferCommissionFieldKeys } from "@/modules/transaction/common/constants";
import {
  CardTransferKeys,
  TransactionTypes,
  TransferKeys
} from "@/modules/transaction/common/enum";
import type { ICardTransferForm, ICardTransferRequest } from "@/modules/transaction/common/types";
import {
  SaveBeneficiaryModal
} from "@/modules/transaction/components/CardTransfer/SaveBeneficiaryModal";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import { useCardTransfer } from "@/modules/transaction/hooks";
import {
  useCreateCardTransferMutation,
  useGetCardTransferQuery,
  useLazyGetCardTransferCommissionQuery,
  useUpdateCardTransferMutation,
} from "@/modules/transaction/services";
import { CURRENCY, DATE_FORMAT, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { BeneficiaryType, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  OwnAccountSelectOption,
  Select,
  TransferBeneficiaries,
  TransferLayout
} from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatCardNumber,
  formatDate,
  isValidAmount,
  mergeByKeys,
  trimAll,
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  accountsList: IAccount[];
  isEdit?: boolean;
  isCopy?: boolean;
  setIsCardNumberInvalid: Dispatch<SetStateAction<boolean>>;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

export const CardTransferForm = ({
  isCopy,
  isEdit,
  accountsList,
  setIsCardNumberInvalid,
  isNameUnique,
  setIsNameUnique,
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { operationId = "" } = useParams();
  const [queryParams] = useSearchParams();
  const idempotencyKey = useRef(uuid4()).current;
  const templateId = queryParams.get(SearchParamKeys.TemplateId);
  const prefilledCard = queryParams.get(SearchParamKeys.BeneficiaryNumber);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    reset,
    values,
    holder,
    errors,
    isOpen,
    refetch,
    setValue,
    setIsOpen,
    fieldChanges,
    setIsBenSaved,
    canGetCommission,
    creditCardNumber,
    handleClickTarget,
    accountsFilteredList,
    handleChangeCardInput,
    handleChangeBeneficiaryCard,
  } = useCardTransfer({
    accountsList,
    setIsCardNumberInvalid,
    commissionFieldKeys: cardTransferCommissionFieldKeys,
  });

  const [getCommission] = useLazyGetCardTransferCommissionQuery();
  const [createCardTransferApi, { isLoading: isCreateTransferLoading }] = useCreateCardTransferMutation();
  const [updateCardTransferApi, { isLoading: isUpdateTransferLoading }] = useUpdateCardTransferMutation();
  const {
    data: cardTransfer,
    isLoading: isGetTransferLoading,
  } = useGetCardTransferQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });
  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetCardTransferTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const cardTransferAction = useCallback((values: ICardTransferForm) => {
    const requestData: ICardTransferRequest = {
      purpose: values.purpose,
      transactionNumber: values.transactionNumber,
      creditCardNumber: trimAll(values.creditCardNumber),
      scheduleDate: formatDate(values.scheduleDate, DATE_FORMAT.YYYY_MM_DD),
      amount: +cleanupAmount(`${values.amount}`),
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    };
    return isEdit ?
      updateCardTransferApi({
        data: requestData,
        id: `${cardTransfer?.id}`
      }).unwrap() :
      createCardTransferApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, cardTransfer]);

  const setDefaultValues = () => {
    const data = {
      creditCardNumber: formatCardNumber({ card: `${cardTransfer?.creditCardNumber}` }),
      debitAccount: cardTransfer?.debitAccount.number,
      transactionNumber: isEdit ? cardTransfer?.transactionNumber : values.transactionNumber,
      beneficiary: cardTransfer?.beneficiary,
      scheduleDate: clampToCurrentDate(cardTransfer?.scheduleDate),
      amount: cardTransfer?.amount,
      fee: cardTransfer?.commissionAmount || 0,
      purpose: cardTransfer?.purpose,
      currency: cardTransfer?.currency,
      commissionAccount: cardTransfer?.commissionAccount?.number,
    };
    handleChangeCardInput(cardTransfer?.creditCardNumber);
    reset?.(data);
    setDefaultData({
      ...data,
      scheduleDate: new Date(cardTransfer?.scheduleDate as string),
    });
  };

  const currentData = useMemo(() => ({
    creditCardNumber: formatCardNumber({ card: `${values?.creditCardNumber}` }),
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    beneficiary: values.beneficiary,
    scheduleDate: new Date(values.scheduleDate),
    amount: +cleanupAmount(values.amount),
    fee: values.fee,
    purpose: values.purpose,
    currency: values.currency,
    commissionAccount: values.commissionAccount,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!cardTransfer?.id) {
      handleChangeBeneficiaryCard(cardTransfer.creditCardNumber);
      setDefaultValues();
    }
  }, [isEdit, isCopy, cardTransfer]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        commissionAccount: template?.commissionAccount?.number,
        debitAccount: template?.debitAccount?.number,
      });
      reset?.(data);
      handleChangeCardInput(template?.creditCardNumber);
      handleChangeBeneficiaryCard(template?.creditCardNumber);
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.card.purpose"));
    }
    if (!isEdit && !isCopy && prefilledCard) {
      const formatted = formatCardNumber({ card: prefilledCard });
      setValue(CardTransferKeys.CreditCardNumber, formatted);
      handleChangeCardInput(prefilledCard);
      handleChangeBeneficiaryCard(prefilledCard);
    }
  }, []);

  return (
    <>
      <TransferLayout
        title={t("transfer.card.title")}
        page="Card transfer"
        isEdit={isEdit}
        hasChanges={hasChanges}
        fieldChanges={fieldChanges}
        accountsList={accountsList}
        getCommission={getCommission}
        canGetCommission={canGetCommission}
        getTransferId={cardTransferAction}
        transactionType={TransactionTypes.CardTransfer}
        isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
        isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
        isLoading={isCreateTransferLoading || isUpdateTransferLoading}
        isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
      >
        <CollapseGroup
          className="collapse-group"
          items={[
            {
              content: <>
                <TransferBeneficiaries
                  name="creditCardNumber"
                  entryType={BeneficiaryType.Card}
                  label={t("transfer.beneficiaryCard")}
                  onChange={(v) => handleChangeBeneficiaryCard(v)}
                  placeholder={t("transfer.typeOrChooseACard")}
                />
                {holder && (
                  <div className="mt-20">
                    <Text>{t("transfer.card.nameOnCard")}</Text>
                    <Text className="my-8">
                      {holder}
                    </Text>
                  </div>
                )}
              </>,
              title: t("general.beneficiaryDetails"),
              value: "beneficiary_details",
              isOpen: true,
            },
            {
              content: <>
                <FormField
                  name="debitAccount"
                  className="mb-32"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      className="custom-select"
                      options={accountsFilteredList}
                      label={t("transfer.debitAccount")}
                      placeholder={t("transfer.chooseDebitAccount")}
                      handleChange={(opt) => handleClickTarget(opt as SingleValue<TOption>)}
                      components={{ Option: OwnAccountSelectOption }}
                    />
                  )}
                />
                <FormField
                  name="amount"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      type="numeric"
                      allowNegative={false}
                      allowLeadingZeros={false}
                      label={t("general.amount")}
                      placeholder={t("general.enterAmount")}
                      thousandSeparator=","
                      decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                      rightIconProps={{
                        Component: CURRENCY[values.currency]?.symbolIcon || "",
                      }}
                      isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                    />
                  )}
                />
                <FormField
                  name="purpose"
                  className="mb-8"
                  errorMessageIcon="infoFilled"
                  hideErrorMessage
                  As={(props) => (
                    <Textarea
                      {...props}
                      required
                      error={`${errors?.purpose?.message || ""}`}
                      label={t("general.purpose")}
                      placeHolder={t("general.enterYourPurpose")}
                      maxCount={INPUT_MAX_LIMIT}
                    />
                  )}
                />
                <SaveAsTemplate
                  setValue={setValue}
                  setIsNameUnique={setIsNameUnique}
                  isSaveAsTemplate={values.isSaveAsTemplate}
                />
              </>,
              title: t("transfer.transferDetails"),
              value: "transfer_details",
              isOpen: true,
            },
            {
              content: <>
                <FormField
                  name="transactionNumber"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      label={t("transfer.transactionNumber")}
                      placeholder={t("transfer.enterTransactionNumber")}
                    />
                  )}/>
                <FormField
                  name="scheduleDate"
                  className="mb-8"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <SimpleDatePicker
                      {...props}
                      required
                      closeOnScroll
                      locale={language}
                      format={DATE_FORMAT.DD_MM_YYYY}
                      label={t("general.date")}
                      value={props.value as Date}
                      minDate={new Date()}
                    />
                  )}
                />
              </>,
              title: t("general.otherDetails"),
              value: "other_details",
              isOpen: true,
            },
          ]}/>
      </TransferLayout>
      <SaveBeneficiaryModal
        isOpen={isOpen}
        refetch={refetch}
        setIsOpen={setIsOpen}
        setIsBenSaved={setIsBenSaved}
        creditCardNumber={creditCardNumber}
      />
    </>
  );
};
